import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { 
    Coins, Calendar, Clock, MapPin, Users, ChevronRight, 
    Sparkles, Heart, Palette, Music, Compass, Star,
    Plus, History, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
    move_well: { icon: Heart, label: 'Move Well', color: 'emerald', gradient: 'from-emerald-500 to-teal-600' },
    connect_well: { icon: Users, label: 'Connect Well', color: 'blue', gradient: 'from-blue-500 to-indigo-600' },
    create_well: { icon: Palette, label: 'Create Well', color: 'purple', gradient: 'from-purple-500 to-pink-600' },
    feel_well: { icon: Sparkles, label: 'Feel Well', color: 'amber', gradient: 'from-amber-500 to-orange-600' },
    explore_well: { icon: Compass, label: 'Explore Well', color: 'cyan', gradient: 'from-cyan-500 to-blue-600' },
    celebrate_well: { icon: Music, label: 'Celebrate Well', color: 'rose', gradient: 'from-rose-500 to-red-600' }
};

function CreditBadge({ credits }) {
    return (
        <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-2xl blur-lg opacity-40"></div>
            <div className="relative bg-gradient-to-r from-amber-400 to-yellow-500 rounded-2xl px-6 py-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                    <Coins className="w-6 h-6 text-amber-900" />
                    <span className="text-4xl font-bold text-amber-900">{credits}</span>
                </div>
                <p className="text-amber-800 text-sm font-medium">Credits Available</p>
            </div>
        </div>
    );
}

function UpcomingBookingCard({ booking }) {
    const activity = booking.activity;
    if (!activity) return null;
    
    const config = CATEGORY_CONFIG[activity.category] || CATEGORY_CONFIG.connect_well;
    const Icon = config.icon;
    const activityDate = new Date(activity.date_time);
    
    return (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-stone-900 truncate">{activity.title}</h4>
                    <div className="flex items-center gap-3 mt-1 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {activityDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {activityDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                    <p className="text-sm text-stone-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {activity.location}
                    </p>
                </div>
            </div>
        </div>
    );
}

function CategoryCard({ category, config, activities, onClick }) {
    const Icon = config.icon;
    const upcomingCount = activities.filter(a => a.category === category).length;
    
    return (
        <button
            onClick={onClick}
            className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 text-left hover:shadow-lg hover:border-stone-300 transition-all group"
            data-testid={`category-${category}`}
        >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
            <h4 className="font-semibold text-stone-900">{config.label}</h4>
            <p className="text-sm text-stone-500 mt-1">{upcomingCount} upcoming</p>
        </button>
    );
}

export function ResidentsDashboardPage() {
    const navigate = useNavigate();
    const { user, token, profile } = useAuth();
    const [upcomingBookings, setUpcomingBookings] = useState([]);
    const [activities, setActivities] = useState([]);
    const [creditHistory, setCreditHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            fetchData();
        }
    }, [token]);

    const fetchData = async () => {
        try {
            const [bookingsRes, activitiesRes, historyRes] = await Promise.all([
                axios.get(`${API}/member/upcoming-bookings`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/activities`),
                axios.get(`${API}/member/credit-history`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            
            setUpcomingBookings(bookingsRes.data);
            setActivities(activitiesRes.data);
            setCreditHistory(historyRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50" data-testid="residents-dashboard-loading">
                <PageHeader title="My Membership" showBack />
                <div className="flex justify-center items-center py-20">
                    <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    if (profile?.membership_status !== 'active') {
        return (
            <div className="min-h-screen bg-stone-50" data-testid="residents-dashboard-inactive">
                <PageHeader title="My Membership" showBack />
                <div className="px-5 py-12 text-center">
                    <div className="w-20 h-20 rounded-full bg-stone-200 flex items-center justify-center mx-auto mb-4">
                        <Star className="w-10 h-10 text-stone-400" />
                    </div>
                    <h2 className="text-xl font-bold text-stone-900 mb-2">Membership Required</h2>
                    <p className="text-stone-600 mb-6">Join the Burston Residents Lifestyle Membership to access activities and experiences.</p>
                    <button
                        onClick={() => navigate('/residents-club')}
                        className="bg-emerald-700 text-white px-6 py-3 rounded-full font-semibold hover:bg-emerald-800 transition-colors"
                    >
                        Learn More
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-50" data-testid="residents-dashboard">
            {/* Premium Header */}
            <div className="bg-gradient-to-br from-emerald-800 via-emerald-700 to-teal-800 text-white">
                <div className="px-5 pt-12 pb-20">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <p className="text-emerald-200 text-sm font-medium">Welcome back</p>
                            <h1 className="text-2xl font-bold">{user?.name}</h1>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                            <span className="text-xl font-bold">{user?.name?.charAt(0)}</span>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Credit Balance Card - overlapping header */}
            <div className="px-5 -mt-12 relative z-10">
                <CreditBadge credits={profile?.credits_balance || 0} />
            </div>
            
            {/* Quick Actions */}
            <div className="px-5 mt-6">
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate('/residents/activities')}
                        className="flex-1 bg-emerald-700 text-white rounded-xl py-3 px-4 font-semibold flex items-center justify-center gap-2 hover:bg-emerald-800 transition-colors"
                        data-testid="browse-activities-btn"
                    >
                        <Calendar className="w-5 h-5" />
                        Browse Activities
                    </button>
                    <button
                        onClick={() => navigate('/residents/bookings')}
                        className="bg-white border-2 border-emerald-700 text-emerald-700 rounded-xl py-3 px-4 font-semibold flex items-center justify-center gap-2 hover:bg-emerald-50 transition-colors"
                        data-testid="my-bookings-btn"
                    >
                        <History className="w-5 h-5" />
                        My Bookings
                    </button>
                </div>
            </div>
            
            {/* Upcoming Bookings */}
            <div className="px-5 mt-8">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-stone-900">Your Upcoming Activities</h2>
                    {upcomingBookings.length > 0 && (
                        <button 
                            onClick={() => navigate('/residents/bookings')}
                            className="text-emerald-700 text-sm font-medium flex items-center gap-1"
                        >
                            View All <ChevronRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
                
                {upcomingBookings.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center">
                        <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-600">No upcoming bookings</p>
                        <button
                            onClick={() => navigate('/residents/activities')}
                            className="text-emerald-700 font-medium mt-2 flex items-center gap-1 mx-auto"
                        >
                            Find an activity <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="upcoming-bookings">
                        {upcomingBookings.slice(0, 3).map((booking) => (
                            <UpcomingBookingCard key={booking.id} booking={booking} />
                        ))}
                    </div>
                )}
            </div>
            
            {/* Browse by Category */}
            <div className="px-5 mt-8 pb-8">
                <h2 className="text-lg font-bold text-stone-900 mb-4">Browse by Category</h2>
                <div className="grid grid-cols-2 gap-3" data-testid="category-grid">
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <CategoryCard
                            key={key}
                            category={key}
                            config={config}
                            activities={activities}
                            onClick={() => navigate(`/residents/activities?category=${key}`)}
                        />
                    ))}
                </div>
            </div>
            
            {/* Buy More Credits */}
            <div className="px-5 pb-8">
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border border-amber-200 p-5">
                    <div className="flex items-start justify-between">
                        <div>
                            <h3 className="font-semibold text-stone-900">Need more credits?</h3>
                            <p className="text-sm text-stone-600 mt-1">Purchase additional credit packs</p>
                            <div className="flex gap-4 mt-3 text-sm">
                                <span className="text-amber-700 font-medium">5 credits = £75</span>
                                <span className="text-amber-700 font-medium">10 credits = £140</span>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/residents/credits')}
                            className="bg-amber-500 text-white rounded-full p-3 hover:bg-amber-600 transition-colors"
                            data-testid="buy-credits-btn"
                        >
                            <Plus className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Recent Credit Activity */}
            {creditHistory.length > 0 && (
                <div className="px-5 pb-24">
                    <h2 className="text-lg font-bold text-stone-900 mb-4">Recent Credit Activity</h2>
                    <div className="bg-white rounded-2xl border border-stone-200 divide-y divide-stone-100" data-testid="credit-history">
                        {creditHistory.slice(0, 5).map((tx) => (
                            <div key={tx.id} className="p-4 flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-stone-900 text-sm">{tx.description}</p>
                                    <p className="text-xs text-stone-500 mt-0.5">
                                        {new Date(tx.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                    </p>
                                </div>
                                <span className={`font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-stone-600'}`}>
                                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ResidentsDashboardPage;
